/**
 * Interactive component router — RN counterpart of the web widget's
 * `components/interactive-flow.tsx`.
 *
 * Everything about sequencing lives here: which step is on screen, what is
 * collected from each one, and how the last one reports back (RPC when the host
 * gave us `onSendResponse`, the legacy `on_complete` API call otherwise). That
 * behaviour is deliberately identical to web — the backend renders against it,
 * so a divergence shows up as a flow that never finishes.
 *
 * Three things could not cross verbatim:
 *  - Web publishes the merged component theme through a React context that the
 *    step files read. RN has no such module, so every step re-derives the same
 *    widget→component mapping from `useWidgetTheme()`. A `theme` on a *flow*
 *    payload therefore styles the card chrome but does not reach the steps; a
 *    `theme` on a single component still reaches its step, because the step
 *    receives that same payload.
 *  - `color-mix(in srgb, surface 50%, background)` has no RN equivalent. The
 *    tint is painted as a half-opaque surface layer over the background, which
 *    is the same sRGB compositing the CSS function performs.
 *  - `AnimatePresence` exits are dropped. RN cannot defer an unmount without a
 *    layout-animation dependency, so steps swap on their enter animation only.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type FC, type ReactNode } from "react";
import { Animated, Linking, StyleSheet, Text, View, type StyleProp, type ViewStyle } from "react-native";
import { Check, ChevronLeft } from "lucide-react-native";

import { buildPayload, formatSlotDate, formatSlotTime, isButtons, isInteractiveFlow, type ComponentActionResponse, type ConfirmationData, type FlowStep, type InteractiveFlowPayload, type SimpleComponentPayload } from "../lib/component-types";
import { GradientFill, solidColor } from "../lib/gradient";
import { ringgId } from "../lib/testing";
import { mergeTheme, toNumber } from "../lib/theme-utils";
import { resolveFontFamily, useWidgetTheme } from "../lib/widget-theme";
import ButtonsStep from "./steps/buttons-step";
import CalendarStep from "./steps/calendar-step";
import ConfirmationStep from "./steps/confirmation-step";
import FormStep from "./steps/form-step";
import { Touchable } from "./ui/pressable";
import { AnimatedSwap } from "./ui/swap";

type FormValue = string | number | boolean | string[];

interface InteractiveFlowProps {
  // Can receive either a single component or a full flow
  initialComponent: SimpleComponentPayload | InteractiveFlowPayload;
  onApiCall: (method: "POST" | "GET", endpoint: string, payload: Record<string, string>) => Promise<ComponentActionResponse>;
  onComplete?: (allData: Record<string, FormValue>) => void;
  // Callback to send response back via RPC (for render_component_<name>_response pattern)
  onSendResponse?: (componentId: string, responseData: Record<string, unknown>) => Promise<void>;
  // When provided, a `selected_item` final-step selection is reported up to the
  // parent instead of being rendered as an in-card pill — the parent can then
  // show the choice as a regular (right-aligned) user chat message.
  onSelectionDisplayed?: (componentId: string, label: string) => void;
  // How free-presentation quick replies align within the panel:
  // "center" for voice mode, "end" (right) for chat mode.
  quickReplyAlign?: "center" | "end";
}

interface EnterFadeProps {
  /** Starting offset in dp; the value animates to 0 as the node fades in. */
  translateX?: number;
  translateY?: number;
  durationMs?: number;
  style?: StyleProp<ViewStyle>;
  testID?: string;
  children?: ReactNode;
}

/** framer-motion's default enter; the step swap overrides it with its own 200ms. */
const ENTER_DURATION_MS = 300;
const STEP_ENTER_DURATION_MS = 200;

const CARD_ENTER_OFFSET = 10;
const STEP_ENTER_OFFSET = 20;
const PILL_ENTER_OFFSET = 8;

/** The `50%` of web's `color-mix`, applied as an alpha instead. */
const SURFACE_TINT_OPACITY = 0.5;

/** `theme.borderRadius` default, kept numeric for the card. */
const DEFAULT_CARD_RADIUS = 8;

/** Best-effort: an unhandled scheme must not take the widget down. */
const openExternalUrl = (url: string): void => {
  void Linking.openURL(url).catch(() => {});
};

const EnterFade: FC<EnterFadeProps> = ({ translateX = 0, translateY = 0, durationMs = ENTER_DURATION_MS, style, testID, children }) => {
  const progress = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(progress, { toValue: 1, duration: durationMs, useNativeDriver: true }).start();
  }, [durationMs, progress]);

  return (
    <Animated.View
      testID={testID}
      style={[
        style,
        {
          opacity: progress,
          transform: [
            { translateX: progress.interpolate({ inputRange: [0, 1], outputRange: [translateX, 0] }) },
            { translateY: progress.interpolate({ inputRange: [0, 1], outputRange: [translateY, 0] }) },
          ],
        },
      ]}
    >
      {children}
    </Animated.View>
  );
};

const InteractiveFlow: FC<InteractiveFlowProps> = ({ initialComponent, onApiCall, onComplete, onSendResponse, onSelectionDisplayed, quickReplyAlign = "end" }) => {
  // Determine if this is a multi-step flow or single component
  const isMultiStepFlow = isInteractiveFlow(initialComponent);
  const isFreeButtonGroup = !isMultiStepFlow && isButtons(initialComponent) && initialComponent.data.presentation === "free";

  // Get widget theme as base, then merge with component theme
  const widgetTheme = useWidgetTheme();

  // Get theme from payload, using widget theme as fallback for colors
  const theme = useMemo(
    () =>
      mergeTheme({
        primaryColor: widgetTheme.primaryColor,
        primaryTextColor: widgetTheme.primaryTextColor,
        backgroundColor: widgetTheme.backgroundColor, // Cards sit on the panel as bordered white surfaces
        surfaceColor: widgetTheme.surfaceColor,
        textColor: widgetTheme.textColor,
        mutedTextColor: widgetTheme.mutedTextColor,
        borderColor: widgetTheme.borderColor,
        ...initialComponent.theme,
      }),
    [initialComponent.theme, widgetTheme],
  );
  const fontFamily = resolveFontFamily(theme.fontFamily);

  // For multi-step flow, extract steps; for single component, wrap it
  const flowSteps: FlowStep[] = isMultiStepFlow ? initialComponent.data.steps : [{ id: "single", title: "", component: initialComponent }];

  const totalSteps = flowSteps.length;

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [allCollectedData, setAllCollectedData] = useState<Record<string, FormValue>>({});
  const [confirmation, setConfirmation] = useState<ConfirmationData | null>(null);
  const [submittedSelection, setSubmittedSelection] = useState<string | null>(null);
  const [stepCompleted, setStepCompleted] = useState<boolean[]>(new Array<boolean>(totalSteps).fill(false));

  const currentStep = flowSteps[currentStepIndex];
  const currentComponent = currentStep?.component;
  const showStepper = totalSteps > 1;

  // Go back to previous step
  const handleBack = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  }, [currentStepIndex]);

  // Move to next step or complete flow
  const handleStepComplete = useCallback(
    async (stepData: Record<string, FormValue>) => {
      setIsProcessing(true);

      // Merge step data
      const newAllData = { ...allCollectedData, ...stepData };
      setAllCollectedData(newAllData);

      // Mark current step as completed
      setStepCompleted((prev) => {
        const updated = [...prev];
        updated[currentStepIndex] = true;
        return updated;
      });

      try {
        const isLastStep = currentStepIndex === totalSteps - 1;
        const selectedButtonId = typeof stepData.selected_button === "string" ? stepData.selected_button : null;
        const buttonsComponent = currentComponent !== undefined && isButtons(currentComponent) ? currentComponent : null;
        const submittedButton =
          buttonsComponent && selectedButtonId ? (buttonsComponent.data.buttons.find((button) => button.id === selectedButtonId) ?? { id: selectedButtonId, label: selectedButtonId, action: { type: "trigger_component" as const } }) : null;
        const shouldShowSubmittedSelection = buttonsComponent?.data.completionDisplay === "selected_item" && !!submittedButton;

        // If using RPC pattern (onSendResponse provided), send via RPC and skip API calls
        if (onSendResponse) {
          // Fast path: when the parent renders the chosen reply as a chat
          // bubble (selected_item + onSelectionDisplayed), show it instantly
          // and unmount the buttons — don't wait for the RPC round-trip.
          const isInstantSelectedItem = isLastStep && shouldShowSubmittedSelection && !!submittedButton && !!onSelectionDisplayed;
          if (isInstantSelectedItem) {
            onSelectionDisplayed(initialComponent.component_id, submittedButton.label);
            onComplete?.(newAllData);
          }

          await onSendResponse(initialComponent.component_id, {
            step_index: currentStepIndex,
            step_data: stepData,
            all_data: newAllData,
            is_final_step: isLastStep,
          });

          if (isLastStep) {
            if (isInstantSelectedItem) {
              // Already handled above — nothing more to do for this step.
            } else if (shouldShowSubmittedSelection && submittedButton) {
              // Fallback (no onSelectionDisplayed callback): render the
              // in-card pill the legacy way.
              setSubmittedSelection(submittedButton.label);
              onComplete?.(newAllData);
            } else {
              // Show confirmation after RPC response is sent
              setConfirmation({
                title: "Success!",
                message: "Your selection has been submitted.",
                icon: "success",
              });
              onComplete?.(newAllData);
            }
          } else {
            // Move to next step
            setCurrentStepIndex((prev) => prev + 1);
          }
        } else {
          // Legacy API call pattern (when onSendResponse is not provided)
          if (isLastStep) {
            // Final step - call the on_complete endpoint if it's a flow
            if (isMultiStepFlow) {
              const { on_complete } = initialComponent.data;
              // Convert all values to strings for legacy API
              const stringifiedData: Record<string, string> = {};
              for (const [key, value] of Object.entries(newAllData)) {
                stringifiedData[key] = Array.isArray(value) ? JSON.stringify(value) : String(value);
              }
              const apiPayload = buildPayload(on_complete.payload, {
                ...stringifiedData,
                all_data: JSON.stringify(newAllData),
              });

              const result = await onApiCall(on_complete.method, on_complete.endpoint, apiPayload);

              if (result.success) {
                setConfirmation(
                  result.confirmation || {
                    title: "Success!",
                    message: result.message,
                    icon: "success",
                  },
                );
                onComplete?.(newAllData);
              } else {
                throw new Error(result.message || "Failed to complete");
              }
            } else {
              // Single component - responses are sent via RPC, just complete
              onComplete?.(newAllData);
            }
          } else {
            // Not last step - move to next
            setCurrentStepIndex((prev) => prev + 1);
          }
        }
      } catch (error) {
        console.error("Error processing step:", error);
        setConfirmation({
          title: "Something went wrong",
          message: (error as Error).message || "Please try again.",
          icon: "error",
        });
      } finally {
        setIsProcessing(false);
      }
    },
    [allCollectedData, currentStepIndex, totalSteps, isMultiStepFlow, initialComponent, currentComponent, onApiCall, onComplete, onSendResponse, onSelectionDisplayed],
  );

  // Handle button clicks (special case)
  const handleButtonClick = useCallback(
    async (buttonId: string, action: { type: string; url?: string; endpoint?: string; method?: "POST" | "GET"; payload?: Record<string, string> }) => {
      if (action.type === "navigate" && action.url) {
        openExternalUrl(action.url);
        return;
      }

      // If using RPC pattern, just send the button selection via RPC
      if (onSendResponse) {
        handleStepComplete({ selected_button: buttonId });
        return;
      }

      // Legacy API call pattern
      if (action.type === "api_call" && action.endpoint) {
        setIsProcessing(true);
        try {
          const result = await onApiCall(action.method || "POST", action.endpoint, action.payload || {});

          if (result.success) {
            // Store button selection and move to next step
            handleStepComplete({ selected_button: buttonId });
          }
        } catch (error) {
          console.error("Error in button action:", error);
        } finally {
          setIsProcessing(false);
        }
      } else {
        // Just move to next step with button selection
        handleStepComplete({ selected_button: buttonId });
      }
    },
    [onApiCall, handleStepComplete, onSendResponse],
  );

  // A flow payload that carries no steps has nothing to render; bail after the
  // hooks so the hook order stays stable.
  if (!currentStep) return null;

  const renderStepComponent = (component: SimpleComponentPayload): ReactNode => {
    switch (component.component_type) {
      case "calendar_booking":
        return (
          <CalendarStep
            payload={component}
            isProcessing={isProcessing}
            onComplete={(slotId, slotDatetime) => {
              const timezone = component.data.timezone;
              handleStepComplete({
                slot_id: slotId,
                slot_datetime: slotDatetime,
                slot_time: formatSlotTime(slotDatetime, timezone),
                slot_date: formatSlotDate(slotDatetime, timezone),
              });
            }}
          />
        );
      case "form":
        return <FormStep payload={component} isProcessing={isProcessing} onComplete={(formData) => handleStepComplete(formData)} />;
      case "buttons":
        return <ButtonsStep payload={component} isProcessing={isProcessing} align={quickReplyAlign} onButtonClick={handleButtonClick} />;
      case "confirmation":
        return <ConfirmationStep data={component.data} />;
      // Block Kit payloads reach the blocks renderer, never this router — web
      // has no branch for them either.
      case "blocks":
      default:
        return null;
    }
  };

  return (
    <EnterFade
      testID={ringgId("interactive-flow")}
      translateY={CARD_ENTER_OFFSET}
      style={[
        styles.root,
        isFreeButtonGroup
          ? null
          : [
              styles.card,
              {
                backgroundColor: solidColor(theme.backgroundColor),
                borderColor: solidColor(theme.borderColor),
                borderRadius: toNumber(theme.borderRadius, DEFAULT_CARD_RADIUS),
              },
            ],
      ]}
    >
      {/* Subtle grey card so widgets (calendar, forms, options) read as distinct
          from the chat — half surface keeps them lighter than user bubbles. */}
      {isFreeButtonGroup ? null : <View pointerEvents="none" style={[StyleSheet.absoluteFill, styles.surfaceTint, { backgroundColor: solidColor(theme.surfaceColor) }]} />}

      {/* Stepper Header - shows total steps from beginning */}
      {showStepper && !confirmation && !submittedSelection && (
        <View testID={ringgId("flow-stepper")} style={styles.stepper}>
          {/* Progress indicators */}
          <View testID={ringgId("flow-stepper-progress")} style={styles.stepperProgress}>
            {flowSteps.map((step, index) => {
              const isCompleted = stepCompleted[index] ?? false;
              const isActive = index === currentStepIndex;
              const indicatorColor = isCompleted ? theme.successColor : isActive ? theme.primaryColor : theme.borderColor;

              return (
                <View key={step.id} testID={ringgId("flow-step")} style={styles.step}>
                  {/* Step indicator. Web's `data-ringg-step-active` /
                      `-completed` have no RN attribute namespace; the active
                      step is exposed through accessibility state instead, and
                      completion reads from the check glyph. */}
                  <View testID={ringgId("flow-step-indicator")} accessibilityState={{ selected: isActive }} style={styles.stepIndicator}>
                    <GradientFill color={indicatorColor} style={StyleSheet.absoluteFill} />
                    {isCompleted ? <Check size={14} color={solidColor(theme.primaryTextColor)} /> : <Text style={[styles.stepIndicatorLabel, { color: isActive ? theme.primaryTextColor : theme.mutedTextColor, fontFamily }]}>{index + 1}</Text>}
                  </View>
                  {/* Connector */}
                  {index < flowSteps.length - 1 && <View testID={ringgId("flow-step-connector")} style={[styles.stepConnector, { backgroundColor: solidColor(isCompleted ? theme.successColor : theme.borderColor) }]} />}
                </View>
              );
            })}
          </View>
          {/* Current step title */}
          <Text testID={ringgId("flow-step-title")} numberOfLines={1} style={[styles.stepTitle, { color: theme.textColor, fontFamily }]}>
            {currentStep.title || `Step ${currentStepIndex + 1} of ${totalSteps}`}
          </Text>
        </View>
      )}

      {/* Content Area */}
      <View testID={ringgId("flow-content")} style={[isFreeButtonGroup ? null : styles.content, !showStepper && !isFreeButtonGroup ? styles.contentTopPadding : null]}>
        {/* Back Button (if not first step and not showing confirmation) */}
        {currentStepIndex > 0 && !confirmation && !submittedSelection && (
          <Touchable onPress={handleBack} testID={ringgId("flow-back-button")} accessibilityLabel="Back" style={styles.backButton}>
            <ChevronLeft size={14} color={solidColor(theme.mutedTextColor)} />
            <Text style={[styles.backLabel, { color: theme.mutedTextColor, fontFamily }]}>Back</Text>
          </Touchable>
        )}

        {/* mode="wait" on web: the outgoing branch finishes leaving before the
            incoming one arrives, so a step advance reads as a hand-off rather
            than a cut. Each branch keeps its own exit vector. */}
        <AnimatedSwap
          swapKey={submittedSelection ? "submitted" : confirmation ? "confirmation" : `step-${currentStepIndex}`}
          durationMs={STEP_ENTER_DURATION_MS}
          enterX={submittedSelection || confirmation ? 0 : STEP_ENTER_OFFSET}
          exitX={submittedSelection || confirmation ? 0 : -STEP_ENTER_OFFSET}
          enterY={submittedSelection ? PILL_ENTER_OFFSET : 0}
          exitY={submittedSelection ? -PILL_ENTER_OFFSET : 0}
        >
        {submittedSelection ? (
          <View testID={ringgId("flow-submitted-selection")} style={styles.submittedSelection}>
            <View testID={ringgId("flow-submitted-pill")} style={[styles.submittedPill, { borderColor: solidColor(theme.primaryColor) }]}>
              <GradientFill color={theme.primaryColor} style={StyleSheet.absoluteFill} />
              <Check size={16} color={solidColor(theme.primaryTextColor)} />
              <Text numberOfLines={1} accessibilityLabel={submittedSelection} style={[styles.submittedLabel, { color: theme.primaryTextColor, fontFamily }]}>
                {submittedSelection}
              </Text>
            </View>
          </View>
        ) : confirmation ? (
          /* Show Confirmation */
          <ConfirmationStep data={confirmation} collectedData={allCollectedData} />
        ) : (
          renderStepComponent(currentStep.component)
        )}
        </AnimatedSwap>
      </View>
    </EnterFade>
  );
};

export default InteractiveFlow;

const styles = StyleSheet.create({
  root: { width: "100%" },
  card: { borderWidth: 1, overflow: "hidden" },
  surfaceTint: { opacity: SURFACE_TINT_OPACITY },
  stepper: { paddingHorizontal: 16, paddingTop: 16, paddingBottom: 12 },
  stepperProgress: { flexDirection: "row", alignItems: "center", gap: 8 },
  step: { flexDirection: "row", alignItems: "center", gap: 8, flex: 1 },
  stepIndicator: { width: 28, height: 28, borderRadius: 9999, alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" },
  stepIndicatorLabel: { fontSize: 12, lineHeight: 16, fontWeight: "600" },
  stepConnector: { flex: 1, height: 2, borderRadius: 9999 },
  stepTitle: { fontSize: 12, lineHeight: 16, fontWeight: "500", marginTop: 12 },
  content: { paddingHorizontal: 16, paddingBottom: 16 },
  contentTopPadding: { paddingTop: 16 },
  // `alignSelf` keeps the touch target on the label: a stretched Pressable
  // would make the whole width of the card tappable, which the <button> is not.
  backButton: { alignSelf: "flex-start", flexDirection: "row", alignItems: "center", gap: 4, marginBottom: 12 },
  backLabel: { fontSize: 12, lineHeight: 16 },
  submittedSelection: { paddingVertical: 8 },
  submittedPill: { alignSelf: "flex-start", maxWidth: "100%", flexDirection: "row", alignItems: "center", gap: 8, borderRadius: 10, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 8, overflow: "hidden" },
  submittedLabel: { flexShrink: 1, fontSize: 14, lineHeight: 20, fontWeight: "600" },
});
